import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronDown, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import {
  useGetCfmReviewBundleQuery,
  useDecideLeaveAsCfmMutation,
  useDecideManagerLeaveAsCfmMutation,
} from "../../slices/leaveApiSlice";
import {
  useGetDepartmentByIdQuery,
  useGetDivisionByIdQuery,
} from "../../slices/userApiSlice";
import CustomSelect from "../../components/CustomSelect/CustomSelect";

// Department Display Component
const DepartmentDisplay = ({ departmentId }) => {
  const {
    data: department,
    isLoading,
    isError,
  } = useGetDepartmentByIdQuery(departmentId, { skip: !departmentId });

  if (isLoading) return <span className="text-gray-500">Loading...</span>;
  if (isError || !department) {
    return <span>Department {departmentId?.substring(0, 8)}</span>;
  }

  return (
    <span>
      {department.name ||
        department.departmentName ||
        `Department ${departmentId?.substring(0, 8)}`}
    </span>
  );
};

// Division Display Component
const DivisionDisplay = ({ divisionId }) => {
  const {
    data: division,
    isLoading,
    isError,
  } = useGetDivisionByIdQuery(divisionId, { skip: !divisionId });

  if (isLoading) return <span className="text-gray-500">Loading...</span>;
  if (isError || !division) {
    return <span>Division {divisionId?.substring(0, 8)}</span>;
  }

  return (
    <span>
      {division.name ||
        division.divisionName ||
        `Division ${divisionId?.substring(0, 8)}`}
    </span>
  );
};
// Add this function after checkIfRejectedByManager function
const checkIfCfmRejected = (leave) => {
  // Check if CFM has rejected this leave
  const cfmNote = leave.cfmNote || "";
  const cfmNoteLower = cfmNote.toLowerCase();

  // Check if CFM note contains rejection keywords
  if (
    cfmNoteLower.includes("reject") ||
    cfmNoteLower.includes("denied") ||
    cfmNoteLower.includes("decline")
  ) {
    return true;
  }

  // Also check in notes array
  if (leave.notes && Array.isArray(leave.notes)) {
    const cfmNotes = leave.notes.filter(
      (note) =>
        (note.actorRole === "CFM" || note.actorRole === "Cfm") &&
        note.message &&
        (note.message.toLowerCase().includes("reject") ||
          note.message.toLowerCase().includes("denied") ||
          note.message.toLowerCase().includes("decline")),
    );

    if (cfmNotes.length > 0) {
      return true;
    }
  }

  // Check status
  if (leave.status === "REJECTED" && leave.cfmNote) {
    return true;
  }

  return false;
};
export default function CfmLeaveRequests() {
  const navigate = useNavigate();

  // Get user info from Redux store (CFM info)
  const { userInfo } = useSelector((state) => state.auth);

  // Extract CFM ID and academy ID from userInfo
  const cfmId = userInfo?.userId || userInfo?.id || userInfo?._id;
  const academyId = userInfo?.academyId;

  // RTK Query hooks - get CFM review bundle
  const {
    data: reviewBundleResponse,
    isLoading,
    isError,
    refetch,
  } = useGetCfmReviewBundleQuery({ cfmId, academyId }, { skip: !cfmId });

  const [decideLeave] = useDecideLeaveAsCfmMutation();
  const [decideManagerLeave] = useDecideManagerLeaveAsCfmMutation();

  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedLeaveType, setSelectedLeaveType] = useState("All");
  const [selectedApplicantType, setSelectedApplicantType] = useState("All");
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showLeaveDetails, setShowLeaveDetails] = useState(false);
  const [actionNote, setActionNote] = useState("");
  const [noteError, setNoteError] = useState("");
  const [localLeaveData, setLocalLeaveData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Update local data when API data changes
  useEffect(() => {
    if (reviewBundleResponse) {
      const allLeaves = [
        ...(reviewBundleResponse.technicianLeaves || []),
        ...(reviewBundleResponse.supervisorLeaves || []),
        ...(reviewBundleResponse.managerLeaves || []),
      ];
      setLocalLeaveData(transformLeaveData(allLeaves));
    }
  }, [reviewBundleResponse]);

  const transformLeaveData = (data) => {
    if (!data) return [];

    const transformed = data.map((leave) => {
      const days = calculateLeaveDays(leave);
      const isSingleDay =
        days === 1 ||
        leave.duration === "FULL_DAY" ||
        leave.duration === "HALF_DAY";

      // Extract supervisor, manager and CFM info from workflow notes if available
      const supervisorInfo = extractSupervisorInfo(leave);
      const managerInfo = extractManagerInfo(leave);
      const cfmInfo = extractCFMInfo(leave);

      const isManualLeaveFlag = isManualLeave(leave.leaveType);
      const leaveTypeDisplay = getLeaveTypeDisplay(leave.leaveType);

      // Use original backend status for internal logic, mapped status for display
      const backendStatus = leave.status;
      const displayStatus = getStatusFromBackend(
        backendStatus,
        leave.leaveType,
      );

      // Enhanced applicant role detection
      const applicantRole = detectApplicantRole(leave);
      const position = getPositionFromRole(applicantRole);

      // Enhanced user email and display extraction
      let userDisplay = "Unknown User";
      let userEmail = "";

      // Try multiple fields to get the email
      if (applicantRole === "MANAGER") {
        userEmail =
          leave.managerEmail ||
          leave.Email ||
          leave.userEmail ||
          leave.email ||
          "";
        userDisplay =
          userEmail ||
          leave.managerName ||
          leave.user?.name ||
          leave.technicianName ||
          "Manager";
      } else if (applicantRole === "SUPERVISOR") {
        userEmail =
          leave.supervisorEmail ||
          leave.Email ||
          leave.userEmail ||
          leave.email ||
          "";
        userDisplay =
          userEmail ||
          leave.supervisorName ||
          leave.user?.name ||
          leave.technicianName ||
          "Supervisor";
      } else {
        // TECHNICIAN
        userEmail = leave.Email || leave.userEmail || leave.email || "";
        userDisplay =
          userEmail || leave.technicianName || leave.user?.name || "Technician";
      }

      // Extract department and division information
      const departmentId = leave.departmentId || leave.user?.departmentId;
      const divisionId = leave.divisionId || leave.user?.divisionId;

      // Check if manager has rejected this leave
      const isRejectedByManager = checkIfRejectedByManager(leave);
      const isRejectedByCfm = checkIfCfmRejected(leave);

      return {
        id: leave.id || leave._id,
        user: userDisplay,
        userEmail: userEmail,
        position: position,
        leaveDisplay: leave.leaveTypeLabel,
        type: leaveTypeDisplay,
        reason: leave.reason || "No reason provided",
        days: leave.totalDays,
        startDate: leave.startDate,
        endDate: leave.endDate,
        submittedDate: leave.submittedAt || leave.createdAt,
        status: displayStatus,
        backendStatus: backendStatus,
        isRejectedByManager: isRejectedByManager, // New flag
        isRejectedByCfm: isRejectedByCfm,

        // Department and Division information
        departmentId: departmentId,
        divisionId: divisionId,

        supervisorNote: leave.supervisorNote || supervisorInfo.note || "",
        supervisorName: leave.supervisorName || supervisorInfo.name || "",
        supervisorEmail: leave.supervisorEmail || supervisorInfo.email || "",
        supervisorRole: "Supervisor",
        supervisorNoteDate: leave.forwardedAt || supervisorInfo.date || "",

        managerNote: leave.managerNote || managerInfo.note || "",
        managerName: leave.managerName || managerInfo.name || "",
        managerEmail: leave.managerEmail || managerInfo.email || "",
        managerRole: "Manager",
        managerNoteDate: leave.decisionAt || managerInfo.date || "",

        cfmNote: leave.cfmNote || cfmInfo.note || "",
        cfmName: leave.cfmName || cfmInfo.name || "",
        cfmEmail: leave.cfmEmail || cfmInfo.email || "",
        cfmNoteDate: cfmInfo.date || "",

        isHalfDay: leave.duration === "HALF_DAY",
        duration: leave.duration,
        halfDaySession: leave.halfDaySession,
        applicantRole: applicantRole,
        originalLeaveType: leave.leaveType,
        isManualLeave: isManualLeaveFlag,
        isSingleDay: isSingleDay,
        originalEndDate: leave.endDate,
        _rawLeave: leave,
      };
    });

    // Sort by submitted date (newest first)
    const sortedData = transformed.sort((a, b) => {
      const dateA = new Date(a.submittedDate);
      const dateB = new Date(b.submittedDate);
      return dateB - dateA;
    });

    return sortedData;
  };

  // Check if leave was rejected by manager (only mark true when manager explicitly rejected)
  const checkIfRejectedByManager = (leave) => {
    // If there's an explicit manager note that indicates rejection, mark as manager-rejected
    if (leave.managerNote) {
      const lowerNote = leave.managerNote.toLowerCase();
      if (
        lowerNote.includes("reject") ||
        lowerNote.includes("denied") ||
        lowerNote.includes("decline")
      ) {
        return true;
      }
    }

    // Check notes array for an entry by MANAGER that indicates rejection
    if (leave.notes && Array.isArray(leave.notes)) {
      const managerNotes = leave.notes.filter(
        (n) => n.actorRole === "MANAGER" || n.actorRole === "Manager",
      );
      if (managerNotes.length > 0) {
        const latestManagerNote = managerNotes[managerNotes.length - 1];
        const msg = (latestManagerNote.message || "").toLowerCase();
        if (
          msg.includes("reject") ||
          msg.includes("denied") ||
          msg.includes("decline")
        ) {
          return true;
        }
      }
    }

    // Don't infer manager rejection solely from backend status because CFM can also set REJECTED
    return false;
  };

  // Enhanced applicant role detection
  const detectApplicantRole = (leave) => {
    if (leave.applicantRole) {
      return leave.applicantRole;
    }

    if (leave.managerId && !leave.supervisorId && !leave.technicianId) {
      return "MANAGER";
    }
    if (leave.supervisorId && !leave.technicianId) {
      return "SUPERVISOR";
    }
    if (leave.managerEmail && !leave.supervisorEmail && !leave.Email) {
      return "MANAGER";
    }
    if (leave.supervisorEmail && !leave.Email) {
      return "SUPERVISOR";
    }
    return "TECHNICIAN";
  };

  const extractSupervisorInfo = (leave) => {
    if (!leave.notes || !Array.isArray(leave.notes)) {
      return { note: "", name: "", email: "", date: "" };
    }

    const supervisorNotes = leave.notes.filter(
      (note) =>
        note.actorRole === "SUPERVISOR" || note.actorRole === "Supervisor",
    );

    if (supervisorNotes.length > 0) {
      const latestNote = supervisorNotes[supervisorNotes.length - 1];
      return {
        note: latestNote.message || "",
        name: latestNote.actorName || "Supervisor",
        email: latestNote.actorEmail || latestNote.actorId || "",
        date: latestNote.timestamp || "",
      };
    }

    return { note: "", name: "", email: "", date: "" };
  };

  const extractManagerInfo = (leave) => {
    if (!leave.notes || !Array.isArray(leave.notes)) {
      return { note: "", name: "", email: "", date: "" };
    }

    const managerNotes = leave.notes.filter(
      (note) => note.actorRole === "MANAGER" || note.actorRole === "Manager",
    );

    if (managerNotes.length > 0) {
      const latestNote = managerNotes[managerNotes.length - 1];
      return {
        note: latestNote.message || "",
        name: latestNote.actorName || "Manager",
        email: latestNote.actorEmail || latestNote.actorId || "",
        date: latestNote.timestamp || "",
      };
    }

    return { note: "", name: "", email: "", date: "" };
  };

  const extractCFMInfo = (leave) => {
    if (!leave.notes || !Array.isArray(leave.notes)) {
      return { note: "", name: "", date: "" };
    }

    const cfmNotes = leave.notes.filter(
      (note) => note.actorRole === "CFM" || note.actorRole === "Cfm",
    );

    if (cfmNotes.length > 0) {
      const latestNote = cfmNotes[cfmNotes.length - 1];
      return {
        note: latestNote.message || "",
        name: latestNote.actorName || "CFM",
        date: latestNote.timestamp || "",
      };
    }

    return { note: "", name: "", date: "" };
  };

  const calculateLeaveDays = (leave) => {
    if (!leave.startDate) return 0;

    switch (leave.duration) {
      case "HALF_DAY":
        return 0.5;
      case "FULL_DAY":
        return 1;
      case "MULTI_DAY":
        if (leave.endDate) {
          const start = new Date(leave.startDate);
          const end = new Date(leave.endDate);
          const diffTime = Math.abs(end - start);
          return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        }
        return 1;
      default:
        return 1;
    }
  };

  const getLeaveTypeDisplay = (backendLeaveType) => {
    if (backendLeaveType === "CASUAL") {
      return "Casual Leave";
    } else if (backendLeaveType === "EARN") {
      return "Earned Leave";
    } else {
      return "Manual Leave";
    }
  };

  const getStatusFromBackend = (backendStatus, leaveType) => {
    if (isManualLeave(leaveType)) {
      return "Approved";
    }

    const statusMap = {
      PENDING: "Pending",
      APPROVED: "Approved",
      REJECTED: "Rejected",
      FORWARDED: "Forwarded",
      ESCALATED: "Approved",
    };

    const normalizedStatus = (backendStatus || "PENDING").toUpperCase();
    return statusMap[normalizedStatus] || "Pending";
  };

  const isManualLeave = (leaveType) => {
    return leaveType !== "CASUAL" && leaveType !== "EARN";
  };

  const getPositionFromRole = (applicantRole) => {
    switch (applicantRole) {
      case "MANAGER":
        return "Manager";
      case "SUPERVISOR":
        return "Supervisor";
      case "TECHNICIAN":
        return "Technician";
      default:
        return "Employee";
    }
  };

  const requiresCfmAction = (leave) => {
    if (!leave) return false;

    if (leave.isManualLeave) return false;

    // If rejected by manager or already rejected by CFM, CFM doesn't need to take action
    if (leave.isRejectedByManager || leave.isRejectedByCfm) return false;

    // If CFM has already taken action, no action needed
    if (
      leave.cfmNote ||
      leave.status === "Approved" ||
      leave.status === "Rejected"
    ) {
      return false;
    }

    // Supervisor leaves that are FORWARDED require CFM action
    if (
      leave.applicantRole === "SUPERVISOR" &&
      leave.backendStatus === "FORWARDED"
    ) {
      return true;
    }
    // Manager leaves that are PENDING require CFM action
    if (
      leave.applicantRole === "MANAGER" &&
      leave.backendStatus === "PENDING"
    ) {
      return true;
    }

    return false;
  };

  const handleBack = () => {
    navigate("/cfm/attendance");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-800";
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "Rejected":
        return "bg-red-100 text-red-800";
      case "Forwarded":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getApplicantTypeColor = (applicantRole) => {
    switch (applicantRole) {
      case "SUPERVISOR":
        return "bg-purple-100 text-purple-800";
      case "MANAGER":
        return "bg-indigo-100 text-indigo-800";
      case "TECHNICIAN":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      // Add 6 hours to the date
      date.setHours(date.getHours() + 6);

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      // Add 6 hours to the date
      date.setHours(date.getHours() + 6);

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const handleRowClick = (leave) => {
    setSelectedLeave(leave);
    setShowLeaveDetails(true);
    setActionNote("");
    setNoteError("");
  };

  const handleCloseDetails = () => {
    setShowLeaveDetails(false);
    setSelectedLeave(null);
    setActionNote("");
    setNoteError("");
    setIsProcessing(false);
  };

  const validateNote = () => {
    if (!actionNote.trim()) {
      setNoteError("Please provide a note for your decision");
      return false;
    }
    setNoteError("");
    return true;
  };

  // Handle CFM decision with immediate success message and modal close
  const handleCfmDecision = async (decision) => {
    if (!validateNote() || !selectedLeave || isProcessing) return;

    setIsProcessing(true);

    try {
      // Determine which API to call based on applicant type
      const apiCall =
        selectedLeave.applicantRole === "MANAGER"
          ? decideManagerLeave
          : decideLeave;

      // Close the modal immediately
      handleCloseDetails();

      // Show success message immediately
      toast.success(`Leave ${decision.toLowerCase()} successfully!`);

      // Make API call in background
      await apiCall({
        leaveId: selectedLeave.id,
        decision: decision,
        note: actionNote,
      }).unwrap();

      // Refetch data
      refetch();
    } catch (error) {
      console.error(`Failed to ${decision.toLowerCase()} leave:`, error);

      // Show error if API call fails
      toast.error("Error", {
        description: `Failed to ${decision.toLowerCase()} leave. Please try again.`,
      });

      setIsProcessing(false);
    }
  };

  // Filtered data - EXCLUDE leaves rejected by manager
  const filteredData = localLeaveData.filter((record) => {
    // Exclude leaves that were rejected by manager
    if (record.isRejectedByManager) {
      return false;
    }

    const statusMatch =
      selectedStatus === "All" ||
      (selectedStatus === "Action Required"
        ? requiresCfmAction(record)
        : record.status === selectedStatus);
    const leaveTypeMatch =
      selectedLeaveType === "All" || record.type === selectedLeaveType;
    const applicantTypeMatch =
      selectedApplicantType === "All" ||
      (selectedApplicantType === "Technician" &&
        record.applicantRole === "TECHNICIAN") ||
      (selectedApplicantType === "Supervisor" &&
        record.applicantRole === "SUPERVISOR") ||
      (selectedApplicantType === "Manager" &&
        record.applicantRole === "MANAGER");

    return statusMatch && leaveTypeMatch && applicantTypeMatch;
  });

  // Add these definitions here
  const statusOptions = [
    { value: "All", label: "All" },
    { value: "Action Required", label: "Action Required" },
    { value: "Pending", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" },
    { value: "Forwarded", label: "Forwarded" },
  ];

  const leaveTypeOptions = [
    { value: "All", label: "All" },
    { value: "Casual Leave", label: "Casual Leave" },
    { value: "Earned Leave", label: "Earned Leave" },
    { value: "Manual Leave", label: "Manual Leave" },
  ];

  const applicantTypeOptions = [
    { value: "All", label: "All" },
    { value: "Technician", label: "Technician" },
    { value: "Supervisor", label: "Supervisor" },
    { value: "Manager", label: "Manager" },
  ];

  // Show loading if CFM info is not available
  if (!cfmId) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading CFM information...</div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading leave requests...</div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">
            Error loading leave requests. Please try again.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-1 mb-4 sm:mb-6">
        <button
          aria-label="Back"
          onClick={handleBack}
          className=" md:flex w-9 h-9 items-center justify-center hover:bg-gray-100 hover:border-gray-400 transition-all rounded-full"
        >
          <ChevronLeft size={24} strokeWidth={2} className="text-gray-700" />
        </button>
        <div className="flex items-center space-x-2 sm:space-x-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
            CFM Leave Requests
          </h1>
        </div>
      </div>

      {/* Records Section */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-zinc-800">
                Leave Review Queue
              </h2>
              <p className="text-sm text-zinc-600 mt-1">
                Review and manage leave applications
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* Status Filter */}
            <div className="flex-1 sm:flex-none">
              <label className="block text-[#ABABAB] text-gray-600 mb-1">
                Status
              </label>
              <CustomSelect
                options={statusOptions}
                value={selectedStatus}
                onChange={setSelectedStatus}
                placeholder="All"
                minWidth={160}
              />
            </div>

            {/* Leave Type Filter */}
            <div className="flex-1 sm:flex-none">
              <label className="block text-[#ABABAB] text-gray-600 mb-1">
                Leave Type
              </label>
              <CustomSelect
                options={leaveTypeOptions}
                value={selectedLeaveType}
                onChange={setSelectedLeaveType}
                placeholder="All"
                minWidth={160}
              />
            </div>

            {/* Applicant Type Filter */}
            <div className="flex-1 sm:flex-none">
              <label className="block text-[#ABABAB] text-gray-600 mb-1">
                Applicant Type
              </label>
              <CustomSelect
                options={applicantTypeOptions}
                value={selectedApplicantType}
                onChange={setSelectedApplicantType}
                placeholder="All"
                minWidth={160}
              />
            </div>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="h-[500px] overflow-auto">
          <table className="w-full">
            <thead className="bg-zinc-50 border-b">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-[#] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  USER
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  TYPE
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  APPLICANT
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  DEPARTMENT
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  DAYS
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  SUBMITTED ON
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  FROM
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  TO
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-zinc-200">
              {filteredData.length === 0 ? (
                <tr>
                  <td
                    colSpan="9"
                    className="px-6 py-8 text-center text-zinc-500"
                  >
                    No leave requests found
                  </td>
                </tr>
              ) : (
                filteredData.map((record) => (
                  <tr
                    key={record.id}
                    className="hover:bg-zinc-50 cursor-pointer transition-colors"
                    onClick={() => handleRowClick(record)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-900">
                      <div>
                        <div>{record.user}</div>
                        <div className="text-sm text-zinc-600">
                          {record.position}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                      {record.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getApplicantTypeColor(record.applicantRole)}`}
                      >
                        {record.applicantRole === "SUPERVISOR"
                          ? "Supervisor"
                          : record.applicantRole === "MANAGER"
                            ? "Manager"
                            : "Technician"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                      {record.departmentId ? (
                        <DepartmentDisplay departmentId={record.departmentId} />
                      ) : (
                        "No Department"
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                      {record.days} {record.days === 1 ? "day" : "days"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {formatDate(record.submittedDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {formatDate(record.startDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {record.isSingleDay
                        ? formatDate(record.startDate)
                        : formatDate(record.endDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}
                      >
                        {record.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Enhanced Leave Details Overlay */}
      {showLeaveDetails && selectedLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-4">
            <div className="p-3 sm:p-4">
              {/* Header with Status and Profile */}
              <div className="flex justify-between items-start mb-3 sm:mb-4">
                <div className="space-y-2 sm:space-y-3">
                  {/* Status and Applicant Badges */}
                  <div className="flex gap-2 flex-wrap">
                    <span
                      className={`inline-flex px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold rounded-full ${getStatusColor(selectedLeave.status)}`}
                    >
                      {selectedLeave.status}
                    </span>
                    <span
                      className={`inline-flex px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold rounded-full ${getApplicantTypeColor(selectedLeave.applicantRole)}`}
                    >
                      {selectedLeave.applicantRole === "SUPERVISOR"
                        ? "Supervisor"
                        : selectedLeave.applicantRole === "MANAGER"
                          ? "Manager"
                          : "Technician"}
                    </span>
                  </div>
                  {/* Employee Profile in Details Overlay */}
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm sm:text-base">
                        {selectedLeave.user.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                        {selectedLeave.userEmail || selectedLeave.user}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600">
                        {selectedLeave.position}
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCloseDetails}
                  className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={isProcessing}
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                </button>
              </div>

              {/* Department and Division Information */}
              <div className="mb-3 sm:mb-4">
                <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                    Department:
                  </span>
                  <span className="text-gray-900 text-sm sm:text-base font-medium">
                    {selectedLeave.departmentId ? (
                      <DepartmentDisplay
                        departmentId={selectedLeave.departmentId}
                      />
                    ) : (
                      "No Department"
                    )}
                  </span>
                </div>
                {selectedLeave.applicantRole === "SUPERVISOR" && (
                  <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                    <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                      Division:
                    </span>
                    <span className="text-gray-900 text-sm sm:text-base font-medium">
                      {selectedLeave.divisionId ? (
                        <DivisionDisplay
                          divisionId={selectedLeave.divisionId}
                        />
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>
                )}
              </div>

              {/* Leave Type */}
              <div className="mb-1">
                <h3 className="font-semibold text-gray-800 text-base sm:text-lg">
                  {selectedLeave.leaveDisplay}
                </h3>
              </div>

              <div className="mb-3 sm:mb-4">
                <h4 className="font-medium text-gray-700 mb-1 sm:mb-2 text-sm sm:text-base">
                  Reason:
                </h4>
                <p className="text-gray-700 bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 text-sm sm:text-base">
                  {selectedLeave.isHalfDay && (
                    <p className="text-sm text-blue-600 mt-1">
                      Half Day -{" "}
                      {selectedLeave.halfDaySession === "MORNING"
                        ? "Morning"
                        : "Afternoon"}{" "}
                      Session
                    </p>
                  )}
                  {selectedLeave.reason}
                </p>
              </div>

              {/* Dates and Days */}
              <div className="mb-3 sm:mb-4">
                <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                    Submitted On:
                  </span>
                  <span className="text-gray-900 text-sm sm:text-base">
                    {formatDateTime(selectedLeave.submittedDate)}
                  </span>
                </div>

                {selectedLeave.isSingleDay ? (
                  <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                    <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                      Leave Date:
                    </span>
                    <span className="text-gray-900 text-sm sm:text-base">
                      {formatDate(selectedLeave.startDate)}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                      <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                        From:
                      </span>
                      <span className="text-gray-900 text-sm sm:text-base">
                        {formatDate(selectedLeave.startDate)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                      <span className="text-xs sm:text-sm font-medium text-gray-700">
                        To:
                      </span>
                      <span className="text-gray-900 text-sm sm:text-base">
                        {formatDate(selectedLeave.endDate)}
                      </span>
                    </div>
                  </>
                )}

                <div className="flex justify-between items-center border-b border-gray-300 pb-2">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                    Days
                  </span>
                  <span className="text-gray-900 text-sm sm:text-base">
                    {selectedLeave.days}{" "}
                    {selectedLeave.days === 1 ? "Day" : "Days"}
                  </span>
                </div>
              </div>

              {/* Notes Section */}
              <div className="bg-gray-50 rounded-lg">
                {selectedLeave.isManualLeave ? (
                  <div className="p-4 text-center">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-800 text-sm sm:text-base mb-2">
                        Manual Leave Approved
                      </h4>
                      <p className="text-green-700 text-sm">
                        Manual leaves are automatically approved and do not
                        require review.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* For Manager Leaves - Show only CFM notes */}
                    {selectedLeave.applicantRole === "MANAGER" ? (
                      <>
                        {selectedLeave.cfmNote && (
                          <div className="p-2 sm:p-3">
                            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                <span className="text-purple-600 font-semibold text-xs sm:text-sm">
                                  {selectedLeave.cfmName?.charAt(0) || "C"}
                                </span>
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                                  {selectedLeave.cfmEmail}
                                </h4>
                                <p className="text-xs sm:text-sm text-gray-600">
                                  CFM
                                </p>
                              </div>
                            </div>
                            <div className="p-1">
                              <p className="text-gray-700 text-sm sm:text-base">
                                {selectedLeave.cfmNote}
                              </p>
                              {selectedLeave.cfmNoteDate && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Note added on:{" "}
                                  {formatDateTime(selectedLeave.cfmNoteDate)}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* If rejected by CFM or manager, show appropriate message */}
                        {selectedLeave.isRejectedByCfm &&
                        selectedLeave.applicantRole === "MANAGER" ? (
                          <div className="p-4 text-center">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                              <h4 className="font-semibold text-red-800 text-sm sm:text-base mb-2">
                                Rejected by CFM
                              </h4>
                              <p className="text-red-700 text-sm">
                                This leave request was rejected by CFM and does
                                not require further CFM review.
                              </p>
                              {selectedLeave.cfmNote && (
                                <div className="mt-3 p-3 bg-white border border-gray-200 rounded">
                                  <p className="text-gray-700 text-sm">
                                    <strong>CFM's Note:</strong>{" "}
                                    {selectedLeave.cfmNote}
                                  </p>
                                  {selectedLeave.cfmNoteDate && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      Rejected on:{" "}
                                      {formatDateTime(
                                        selectedLeave.cfmNoteDate,
                                      )}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : selectedLeave.isRejectedByManager ? (
                          <div className="p-4 text-center">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                              <h4 className="font-semibold text-red-800 text-sm sm:text-base mb-2">
                                Rejected by Manager
                              </h4>
                              <p className="text-red-700 text-sm">
                                This leave request has been rejected by the
                                manager and does not require CFM review.
                              </p>
                              {selectedLeave.managerNote && (
                                <div className="mt-3 p-3 bg-white border border-gray-200 rounded">
                                  <p className="text-gray-700 text-sm">
                                    <strong>Manager's Note:</strong>{" "}
                                    {selectedLeave.managerNote}
                                  </p>
                                  {selectedLeave.managerNoteDate && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      Rejected on:{" "}
                                      {formatDateTime(
                                        selectedLeave.managerNoteDate,
                                      )}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* Supervisor Note - Show for Technician applications */}
                            {selectedLeave.applicantRole === "TECHNICIAN" &&
                              selectedLeave.supervisorNote && (
                                <div className="p-2 sm:p-3">
                                  <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                      <span className="text-blue-600 font-semibold text-xs sm:text-sm">
                                        {selectedLeave.supervisorName?.charAt(
                                          0,
                                        ) || "S"}
                                      </span>
                                    </div>
                                    <div>
                                      <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                                        {selectedLeave.supervisorEmail ||
                                          "Supervisor"}
                                      </h4>
                                      <p className="text-xs sm:text-sm text-gray-600">
                                        Supervisor
                                      </p>
                                    </div>
                                  </div>
                                  <div className="p-1">
                                    <p className="text-gray-700 text-sm sm:text-base">
                                      {selectedLeave.supervisorNote}
                                    </p>
                                    {selectedLeave.supervisorNoteDate && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        Note added on:{" "}
                                        {formatDateTime(
                                          selectedLeave.supervisorNoteDate,
                                        )}
                                      </p>
                                    )}
                                  </div>

                                  {selectedLeave.managerNote && (
                                    <div className="pb-2 mb-2">
                                      <div className="border-b border-gray-300 pb-2 mt-2 mb-1"></div>
                                    </div>
                                  )}
                                </div>
                              )}

                            {/* Manager Note - Show if exists */}
                            {selectedLeave.managerNote && (
                              <div className="p-2 sm:p-3">
                                <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center">
                                    <span className="text-green-600 font-semibold text-xs sm:text-sm">
                                      {selectedLeave.managerName?.charAt(0) ||
                                        "M"}
                                    </span>
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                                      {selectedLeave.managerEmail}
                                    </h4>
                                    {selectedLeave.managerRole && (
                                      <p className="text-xs text-gray-500">
                                        {selectedLeave.managerRole}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="p-1">
                                  <p className="text-gray-700 text-sm sm:text-base">
                                    {selectedLeave.managerNote}
                                  </p>
                                  {selectedLeave.managerNoteDate && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      Note added on:{" "}
                                      {formatDateTime(
                                        selectedLeave.managerNoteDate,
                                      )}
                                    </p>
                                  )}
                                </div>
                                {selectedLeave.cfmNote && (
                                  <div className="pb-2 mb-2">
                                    <div className="border-b border-gray-300 pb-2 mt-2 mb-1"></div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* CFM Note - Show if exists */}
                            {selectedLeave.cfmNote && (
                              <div className="p-2 sm:p-3">
                                <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                    <span className="text-purple-600 font-semibold text-xs sm:text-sm">
                                      {selectedLeave.cfmName?.charAt(0) || "C"}
                                    </span>
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                                      {selectedLeave.cfmEmail}
                                    </h4>
                                    <p className="text-xs sm:text-sm text-gray-600">
                                      CFM
                                    </p>
                                  </div>
                                </div>
                                <div className="p-1">
                                  <p className="text-gray-700 text-sm sm:text-base">
                                    {selectedLeave.cfmNote}
                                  </p>
                                  {selectedLeave.cfmNoteDate && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      Note added on:{" "}
                                      {formatDateTime(
                                        selectedLeave.cfmNoteDate,
                                      )}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}

                            {!selectedLeave.supervisorNote &&
                              !selectedLeave.managerNote &&
                              !selectedLeave.cfmNote && (
                                <div className="p-4 text-center">
                                  <p className="text-gray-600 text-sm">
                                    No review notes available for this leave
                                    request.
                                  </p>
                                </div>
                              )}
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Action Section for CFM - Only show if not rejected by manager */}
              {requiresCfmAction(selectedLeave) && (
                <div className="space-y-4 mt-4">
                  {/* Note Input with Validation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Note for your decision
                    </label>
                    <textarea
                      value={actionNote}
                      onChange={(e) => {
                        setActionNote(e.target.value);
                        if (noteError && e.target.value.trim()) {
                          setNoteError("");
                        }
                      }}
                      rows={3}
                      placeholder="Enter your note here..."
                      className={`w-full border-2 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base ${
                        noteError ? "border-red-500" : "border-gray-300"
                      }`}
                      disabled={isProcessing}
                    />
                    {noteError && (
                      <p className="text-red-500 text-[#ABABAB] mt-1 flex items-center">
                        <span className="mr-1">⚠</span>
                        {noteError}
                      </p>
                    )}
                  </div>

                  {/* Action Buttons for CFM */}
                  <div className="flex space-x-3 justify-center">
                    <button
                      onClick={() => handleCfmDecision("REJECT")}
                      disabled={isProcessing}
                      className={`px-8 py-3 rounded-lg font-semibold transition-colors text-sm sm:text-base whitespace-nowrap ${
                        isProcessing
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-red-600 hover:bg-red-700 text-white"
                      }`}
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => handleCfmDecision("APPROVE")}
                      disabled={isProcessing}
                      className={`px-8 py-3 rounded-lg font-semibold transition-colors text-sm sm:text-base whitespace-nowrap ${
                        isProcessing
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700 text-white"
                      }`}
                    >
                      Approve
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
